/**
 * Operational Exception Center
 * 
 * The operational inbox for the agency — one central place showing everything that needs attention.
 * Instead of staff checking multiple modules, Aligned surfaces every issue requiring attention in one place.
 * 
 * Features:
 * - Exception Resolution Assistant (Why this happened + Recommended Action)
 * - Category-based filtering (Caregiver, Scheduling, Payroll, Billing, Compliance, Client Care, Agency)
 * - Priority-based sorting (Critical, High, Medium, Low)
 * - One-click resolution workflows
 * 
 * Philosophy: No silent failures. Every exception includes context and guidance.
 */
import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  AlertCircle, Clock, DollarSign, FileText, Shield, Heart, Building,
  Calendar, User, ChevronRight, CheckCircle, AlertTriangle, Loader2,
  RefreshCw, Search, Filter, X, MapPin, Phone, ExternalLink, 
  Bell, Users, ClipboardList, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

// Category icons and colors
const CATEGORY_CONFIG = {
  caregiver: { icon: User, color: "bg-blue-100 text-blue-700", label: "Caregiver" },
  scheduling: { icon: Calendar, color: "bg-purple-100 text-purple-700", label: "Scheduling" },
  payroll: { icon: DollarSign, color: "bg-emerald-100 text-emerald-700", label: "Payroll" },
  billing: { icon: FileText, color: "bg-amber-100 text-amber-700", label: "Billing" },
  compliance: { icon: Shield, color: "bg-red-100 text-red-700", label: "Compliance" },
  client_care: { icon: Heart, color: "bg-pink-100 text-pink-700", label: "Client Care" },
  agency: { icon: Building, color: "bg-gray-100 text-gray-700", label: "Agency" },
};

// Priority colors and labels
const PRIORITY_CONFIG = {
  critical: { color: "bg-red-500 text-white", label: "Critical", dotColor: "bg-red-500" },
  high: { color: "bg-orange-500 text-white", label: "High", dotColor: "bg-orange-500" },
  medium: { color: "bg-amber-500 text-white", label: "Medium", dotColor: "bg-amber-500" },
  low: { color: "bg-gray-400 text-white", label: "Low", dotColor: "bg-gray-400" },
};

// Exception card with Resolution Assistant
const ExceptionCard = ({ exception, onResolve, expanded, onToggleExpand }) => {
  const CategoryIcon = CATEGORY_CONFIG[exception.category]?.icon || AlertCircle;
  const priorityConfig = PRIORITY_CONFIG[exception.priority] || PRIORITY_CONFIG.medium;
  const categoryConfig = CATEGORY_CONFIG[exception.category] || CATEGORY_CONFIG.agency;

  return (
    <div 
      className={`bg-white rounded-xl border ${exception.priority === 'critical' ? 'border-red-300 shadow-red-50' : 'border-gray-200'} overflow-hidden transition-all duration-200 hover:shadow-md`}
      data-testid={`exception-card-${exception.id}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            {/* Priority dot */}
            <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${priorityConfig.dotColor}`} />
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">{exception.title}</h3>
                <Badge className={priorityConfig.color + " text-xs"}>
                  {priorityConfig.label}
                </Badge>
                <Badge className={categoryConfig.color + " text-xs"}>
                  <CategoryIcon className="w-3 h-3 mr-1" />
                  {categoryConfig.label}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 mt-1">{exception.description}</p>
              
              {/* Context chips */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {exception.client_name && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                    <Heart className="w-3 h-3" />
                    {exception.client_name}
                  </span>
                )}
                {exception.caregiver_name && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {exception.caregiver_name}
                  </span>
                )}
                {exception.due_date && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Due: {new Date(exception.due_date).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleExpand(exception.id)}
              className="text-gray-500"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded: Resolution Assistant */}
      {expanded && (
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          {/* Why This Happened */}
          {exception.why_this_happened && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                Why This Happened
              </h4>
              <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border border-gray-200">
                {exception.why_this_happened}
              </p>
            </div>
          )}

          {/* Recommended Action */}
          {exception.recommended_action && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Recommended Action
              </h4>
              <p className="text-sm text-gray-600 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                {exception.recommended_action}
              </p>
            </div>
          )}

          {/* Action Steps */}
          {exception.action_steps && exception.action_steps.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Steps to Resolve</h4>
              <ol className="list-decimal list-inside space-y-1">
                {exception.action_steps.map((step, idx) => (
                  <li key={idx} className="text-sm text-gray-600">{step}</li>
                ))}
              </ol>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            {exception.action_url && (
              <Button
                size="sm"
                className="bg-[#06372B] hover:bg-[#0a4a3a]"
                onClick={() => window.location.href = exception.action_url}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Take Action
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResolve(exception)}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Resolved
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary stats card
const StatCard = ({ label, value, icon: Icon, color, onClick, active }) => (
  <button
    onClick={onClick}
    className={`bg-white rounded-xl border p-4 text-left transition-all hover:shadow-md w-full ${
      active ? 'ring-2 ring-[#06372B] border-[#06372B]' : 'border-gray-200'
    }`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </button>
);

// Resolution modal
const ResolveModal = ({ exception, onClose, onConfirm }) => {
  const [resolveType, setResolveType] = useState("resolved");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm({
      resolution_type: resolveType,
      resolution_notes: notes,
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Resolve Exception</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution Type
              </label>
              <Select value={resolveType} onValueChange={setResolveType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="resolved">Resolved - Issue was fixed</SelectItem>
                  <SelectItem value="dismissed">Dismissed - Not a real issue</SelectItem>
                  <SelectItem value="escalated">Escalated - Forwarded to supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe how this was resolved..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#06372B] focus:border-transparent"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={loading}
              className="bg-[#06372B] hover:bg-[#0a4a3a]"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Resolution
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Exception Center component
export default function ExceptionCenter() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activePriority, setActivePriority] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [resolvingException, setResolvingException] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get("/exceptions/dashboard");
      setDashboard(res.data);
    } catch (err) {
      console.error("Failed to load exceptions:", err);
      toast.error("Failed to load exceptions");
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (exception) => {
    setResolvingException(exception);
  };

  const confirmResolve = async (resolution) => {
    try {
      await api.post(`/exceptions/${resolvingException.id}/resolve`, resolution);
      toast.success("Exception resolved");
      setResolvingException(null);
      loadDashboard();
    } catch (err) {
      toast.error("Failed to resolve exception");
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Filter exceptions
  const filteredExceptions = dashboard?.exceptions?.filter(exc => {
    if (activeCategory !== "all" && exc.category !== activeCategory) return false;
    if (activePriority !== "all" && exc.priority !== activePriority) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        exc.title?.toLowerCase().includes(query) ||
        exc.description?.toLowerCase().includes(query) ||
        exc.client_name?.toLowerCase().includes(query) ||
        exc.caregiver_name?.toLowerCase().includes(query)
      );
    }
    return true;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }

  const { priority_counts = {}, by_category = {} } = dashboard || {};

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="exception-center">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Exception Center</h1>
            <p className="text-gray-500">
              Your operational inbox — everything that needs attention in one place
            </p>
          </div>
        </div>
      </div>

      {/* Priority Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Critical"
          value={priority_counts.critical || 0}
          icon={AlertTriangle}
          color="bg-red-500"
          onClick={() => setActivePriority(activePriority === "critical" ? "all" : "critical")}
          active={activePriority === "critical"}
        />
        <StatCard
          label="High Priority"
          value={priority_counts.high || 0}
          icon={AlertCircle}
          color="bg-orange-500"
          onClick={() => setActivePriority(activePriority === "high" ? "all" : "high")}
          active={activePriority === "high"}
        />
        <StatCard
          label="Medium"
          value={priority_counts.medium || 0}
          icon={Clock}
          color="bg-amber-500"
          onClick={() => setActivePriority(activePriority === "medium" ? "all" : "medium")}
          active={activePriority === "medium"}
        />
        <StatCard
          label="Low"
          value={priority_counts.low || 0}
          icon={CheckCircle}
          color="bg-gray-400"
          onClick={() => setActivePriority(activePriority === "low" ? "all" : "low")}
          active={activePriority === "low"}
        />
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 mb-6">
        <div className="flex items-center gap-1 overflow-x-auto">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeCategory === "all"
                ? "bg-[#06372B] text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            All ({dashboard?.total_exceptions || 0})
          </button>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
            const count = by_category[key]?.count || 0;
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(activeCategory === key ? "all" : key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeCategory === key
                    ? "bg-[#06372B] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {config.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Refresh */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search exceptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={loadDashboard}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Exception List */}
      {filteredExceptions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">All Clear!</h3>
          <p className="text-gray-500">
            {activeCategory !== "all" || activePriority !== "all" || searchQuery
              ? "No exceptions match your filters"
              : "No operational exceptions at this time"}
          </p>
          {(activeCategory !== "all" || activePriority !== "all" || searchQuery) && (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setActiveCategory("all");
                setActivePriority("all");
                setSearchQuery("");
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredExceptions.map((exception) => (
            <ExceptionCard
              key={exception.id}
              exception={exception}
              expanded={expandedId === exception.id}
              onToggleExpand={toggleExpand}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {/* Resolution Modal */}
      {resolvingException && (
        <ResolveModal
          exception={resolvingException}
          onClose={() => setResolvingException(null)}
          onConfirm={confirmResolve}
        />
      )}
    </div>
  );
}
